import React, { useEffect, useState } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Super_admin_web_protal from '../../../layout/Super_admin_web_portal';
import * as XLSX from 'xlsx';
import Input from '../../../components/Input';
import { toast } from 'react-toastify';
import {
  CompletedPlanHeaderSkeleton,
  CompletedPlanTableSkeleton
} from '../../../components/Skeleton';
import {
  buildClosedEnrollmentCacheKey,
  clearClosedEnrollmentCache,
  getClosedEnrollmentCache,
  setClosedEnrollmentCache
} from '../../../utils/closedEnrollmentCache';
import { Base_url } from '../../../utils/Base_url';

const API_BASE = `${Base_url}/v1/worker`;
const perPage = 8;

const SuperCompletedPlan = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmationStep, setDeleteConfirmationStep] = useState(1); // 1 = first confirmation, 2 = second confirmation
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    investorName: '',
    totalDeposit: '',
    totalProfit: '',
    totalLoss: '',
    totalDeduction: '',
    numberOfYears: '',
    paymentFrequency: '',
    status: '',
    paymentStatus: '',
    remarks: '',
    closingDate: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const fetchClosedEnrollments = async ({
    pageNum = page,
    searchTerm = debouncedSearch,
    paymentStatus = statusFilter,
    signal
  } = {}) => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(perPage),
      paymentStatus: paymentStatus || 'all'
    });
    if (searchTerm) params.set('search', searchTerm);

    const res = await fetch(`${API_BASE}/closed-enrollement?${params.toString()}`, {
      signal
    });
    if (!res.ok) throw new Error('Server error');
    return res.json();
  };

  const applyPageResult = (json) => {
    if (json.success && Array.isArray(json.data)) {
      setData(json.data);
      setTotal(json.total || 0);
      setTotalPages(json.totalPages || 1);
      return true;
    }
    setData([]);
    setTotal(0);
    setTotalPages(1);
    return false;
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const cacheKey = buildClosedEnrollmentCacheKey({
      page,
      search: debouncedSearch,
      paymentStatus: statusFilter,
      limit: perPage
    });

    const cached = getClosedEnrollmentCache(cacheKey);
    if (cached) {
      applyPageResult(cached);
      setError(null);
      setLoading(false);
      return () => {
        active = false;
        controller.abort();
      };
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const json = await fetchClosedEnrollments({
          pageNum: page,
          searchTerm: debouncedSearch,
          paymentStatus: statusFilter,
          signal: controller.signal
        });

        if (!active) return;

        applyPageResult(json);
        if (json.success && Array.isArray(json.data)) {
          setClosedEnrollmentCache(cacheKey, json);
        }
      } catch (err) {
        if (err.name === 'AbortError' || !active) return;
        setError('Failed to load data. Check internet or try again.');
        setData([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [page, debouncedSearch, statusFilter]);

  const refreshList = async () => {
    clearClosedEnrollmentCache();
    setLoading(true);
    try {
      const json = await fetchClosedEnrollments();
      applyPageResult(json);
      if (json.success && Array.isArray(json.data)) {
        const cacheKey = buildClosedEnrollmentCacheKey({
          page,
          search: debouncedSearch,
          paymentStatus: statusFilter,
          limit: perPage
        });
        setClosedEnrollmentCache(cacheKey, json);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        export: 'true',
        paymentStatus: statusFilter || 'all'
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await fetch(`${API_BASE}/closed-enrollement?${params.toString()}`);
      const json = await res.json();

      if (!json.success || !Array.isArray(json.data) || json.data.length === 0) {
        alert('No data to export');
        return;
      }

      const exportData = json.data.map((item) => ({
        'Investor Name': item.investorName,
        CNIC: item.investorCnic,
        'Plan Name': item.planName || 'N/A',
        'Total Deposit': item.totalDepositedAmount ?? item.totalDeposit,
        Profit: item.totalProfit,
        Loss: item.totalLoss || 0,
        Deduction: item.totalDeduction,
        'Net Amount':
          (item.totalDepositedAmount ?? item.totalDeposit ?? 0) +
          (item.totalProfit || 0) -
          (item.totalLoss || 0) -
          (item.totalDeduction || 0),
        Years: item.numberOfYears,
        'Payment Frequency': item.paymentFrequency,
        Status: item.status,
        'Payment Status': item.paymentStatus || 'pending',
        'Deposit Date': item.firstDepositDate
          ? new Date(item.firstDepositDate).toLocaleDateString()
          : new Date(item.enrollmentDate).toLocaleDateString(),
        'Closing Date': item.closingDate ? new Date(item.closingDate).toLocaleDateString() : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Closed Enrollments');
      XLSX.writeFile(wb, `Closed_Enrollments_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK');
  };

  // Open edit modal
  const openEditModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setEditFormData({
      investorName: enrollment.investorName || '',
      totalDeposit: enrollment.totalDepositedAmount?.toString() || '',
      totalProfit: enrollment.totalProfit?.toString() || '',
      totalLoss: enrollment.totalLoss?.toString() || '0',
      totalDeduction: enrollment.totalDeduction?.toString() || '',
      numberOfYears: enrollment.numberOfYears?.toString() || '',
      paymentFrequency: enrollment.paymentFrequency || '',
      status: enrollment.status || 'closed',
      paymentStatus: enrollment.paymentStatus || 'pending',
      remarks: enrollment.remarks || '',
      closingDate: enrollment.closingDate ? new Date(enrollment.closingDate).toISOString().split('T')[0] : ''
    });
    setIsEditModalOpen(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedEnrollment(null);
    setEditFormData({
      investorName: '',
      totalDeposit: '',
      totalProfit: '',
      totalLoss: '',
      totalDeduction: '',
      numberOfYears: '',
      paymentFrequency: '',
      status: '',
      paymentStatus: '',
      remarks: '',
      closingDate: ''
    });
  };

  // Handle form input change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Update enrollment
  const handleUpdateEnrollment = async () => {
    if (!selectedEnrollment?._id) {
      toast.error('No enrollment selected');
      return;
    }

    setIsUpdating(true);

    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        investorName: editFormData.investorName,
        totalDeposit: parseFloat(editFormData.totalDeposit) || 0,
        totalProfit: parseFloat(editFormData.totalProfit) || 0,
        totalLoss: parseFloat(editFormData.totalLoss) || 0,
        totalDeduction: parseFloat(editFormData.totalDeduction) || 0,
        numberOfYears: parseInt(editFormData.numberOfYears) || 0,
        paymentFrequency: editFormData.paymentFrequency,
        status: editFormData.status,
        paymentStatus: editFormData.paymentStatus || 'pending',
        remarks: editFormData.remarks || '',
        closingDate: editFormData.closingDate ? new Date(editFormData.closingDate).toISOString() : null
      });

      const requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      const response = await fetch(
        `${Base_url}/v1/worker/update-enrollement/${selectedEnrollment._id}`,
        requestOptions
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Enrollment updated successfully!');
        closeEditModal();
        await refreshList();
      } else {
        toast.error(result.message || 'Failed to update enrollment');
      }
    } catch (error) {
      console.error('Error updating enrollment:', error);
      toast.error('Failed to update enrollment. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setDeleteConfirmationStep(1);
    setIsDeleteModalOpen(true);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedEnrollment(null);
    setDeleteConfirmationStep(1);
    setIsDeleting(false);
  };

  // Handle first confirmation
  const handleFirstConfirm = () => {
    setDeleteConfirmationStep(2);
  };

  // Handle delete enrollment
  const handleDeleteEnrollment = async () => {
    if (!selectedEnrollment?._id) {
      toast.error('No enrollment selected');
      return;
    }

    setIsDeleting(true);

    try {
      const requestOptions = {
        method: "DELETE",
        redirect: "follow"
      };

      const response = await fetch(
        `${Base_url}/v1/worker/delete-enrollement/${selectedEnrollment._id}`,
        requestOptions
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Enrollment deleted successfully!');
        closeDeleteModal();
        if (data.length <= 1 && page > 1) {
          setPage((p) => p - 1);
        } else {
          await refreshList();
        }
      } else {
        toast.error(result.message || 'Failed to delete enrollment');
      }
    } catch (error) {
      console.error('Error deleting enrollment:', error);
      toast.error('Failed to delete enrollment. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Super_admin_web_protal
      language={<p className="sm:block hidden">English | Urdu</p>}
      ToggleBtn={
        <div className="flex gap-4 items-center">
          <HiOutlineArrowLeft />
          <h2 className="text-xl font-semibold">Closed Enrollments</h2>
        </div>
      }
      title_props={
        <div className="w-96 relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
          />
          <button className="absolute right-1 top-1 bg-secondary hover:bg-primary text-white px-4 py-2 rounded-full transition-colors">
            <IoSearchOutline size={20} />
          </button>
        </div>
      }
    >

      {/* Mobile Search */}
      <div className="w-full mb-6 sm:hidden block relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, CNIC, plan..."
          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
        />
        <button className="absolute right-1 top-1 bg-secondary hover:bg-primary text-white px-4 py-2 rounded-full transition-colors">
          <IoSearchOutline size={20} />
        </button>
      </div>

      {loading ? (
        <CompletedPlanHeaderSkeleton showFilters />
      ) : (
        <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 hidden sm:block">Closed Enrollments</h2>
          <p className="text-sm text-gray-600 mt-1">
            Showing {total} closed enrollment{total !== 1 ? 's' : ''}
            {debouncedSearch && ` for "${debouncedSearch}"`}
          </p>
        </div>
        <button
          onClick={exportExcel}
          disabled={isExporting || total === 0}
          className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 transition-colors ${
            isExporting || total === 0
              ? 'bg-green-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {isExporting ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {/* Payment Status Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'paid', 'adjusted', 'partial paid', 're invested'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors capitalize ${
              statusFilter === status
                ? 'bg-secondary text-white border-secondary'
                : 'bg-white text-gray-600 border-gray-300 hover:border-secondary hover:text-secondary'
            }`}
          >
            {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>
        </>
      )}

      {/* Loading Skeleton */}
      {loading && <CompletedPlanTableSkeleton rows={perPage} showActions />}

      {/* Error */}
      {error && !loading && (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-secondary hover:bg-primary text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* No Data */}
      {!loading && !error && data.length === 0 && (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-xl text-gray-500 mb-2">No closed enrollments found</p>
          <p className="text-gray-400">
            {debouncedSearch ? 'Try a different search term' : 'All plans are currently active'}
          </p>
        </div>
      )}

      {/* Real Table - Only show when not loading, no error, and has data */}
      {!loading && !error && data.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200">
            <div className="min-w-[1400px]"> {/* Increased width to accommodate loss column */}
              <table className="w-full bg-white">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[180px]">Investor Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]">CNIC</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Plan Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">Deposit</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Profit</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Loss</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Deduction</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">Net Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[100px]">Years</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px] whitespace-nowrap">Payment Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[180px] whitespace-nowrap">Deposit Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[160px] whitespace-nowrap">Closing Date</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.map((item) => {
                    const loss = item.totalLoss || 0;
                    const net = item.totalDepositedAmount + item.totalProfit - loss - item.totalDeduction;
                    return (
                      <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                          <Link
                            onClick={() => localStorage.setItem('iName', item?.investorName)}
                            to={`/super_admin/closed-deposits/${item?.investorCnic}/${item?._id}`}
                            className="font-medium text-secondary hover:text-primary transition-colors"
                          >
                            {item.investorName}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[150px]">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {item.investorCnic}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {item.planName || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[160px]">
                          <div className="text-sm text-gray-900 font-medium">
                            Rs. {formatCurrency(item.totalDepositedAmount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <div className="text-sm text-green-600 font-medium">
                            +Rs. {formatCurrency(item.totalProfit)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <div className={`text-sm font-medium ${loss > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {loss > 0 ? `-Rs. ${formatCurrency(loss)}` : 'Rs. 0'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <div className="text-sm text-red-600 font-medium">
                            -Rs. {formatCurrency(item.totalDeduction)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[160px]">
                          <div className={`text-sm font-bold px-3 py-2 rounded-lg ${net >= 0 ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
                            }`}>
                            Rs. {formatCurrency(net)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                            {item.numberOfYears} {item.numberOfYears === 1 ? 'year' : 'years'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            (item.paymentStatus || 'pending') === 'pending'
                              ? 'bg-amber-100 text-amber-800'
                              : (item.paymentStatus || '').toLowerCase() === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : (item.paymentStatus || '').toLowerCase() === 're invested'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(item.paymentStatus || 'pending')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-900 whitespace-nowrap">
                              {formatDate(item.firstDepositDate || item.enrollmentDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[160px]">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-900 whitespace-nowrap">
                              {item.closingDate ? formatDate(item.closingDate) : <span className="text-gray-400 italic">N/A</span>}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                          <div className="flex justify-center items-center gap-2">
                            <button
                              onClick={() => openEditModal(item)}
                              className="bg-[#00AFEF] text-white px-4 py-2 rounded-md text-sm transition-colors shadow-sm flex items-center gap-2 hover:opacity-90"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => openDeleteModal(item)}
                              className="bg-[red] text-white px-4 py-2 rounded-md text-sm transition-colors shadow-sm flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-6 px-2">
            <div className="text-sm text-gray-600">
              Showing {total === 0 ? 0 : (page - 1) * perPage + 1} to{' '}
              {Math.min(page * perPage, total)} of {total} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page === 1}
                title="First page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${page === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                  }`}
              >
                <FaAngleDoubleLeft />
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                title="Previous page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${page === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                  }`}
              >
                <FaAngleLeft />
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-colors ${page === pageNum
                        ? 'bg-secondary text-white shadow-sm'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                title="Next page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${page === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                  }`}
              >
                <FaAngleRight />
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || totalPages <= 1}
                title="Last page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${page === totalPages || totalPages <= 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                  }`}
              >
                <FaAngleDoubleRight />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit Enrollment Modal */}
      {isEditModalOpen && selectedEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Edit Enrollment</h3>
                <button
                  onClick={closeEditModal}
                  disabled={isUpdating}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Enrollment Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-3">Enrollment Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">

                  <div>
                    <span className="text-gray-500">CNIC:</span>
                    <p className="font-medium">{selectedEnrollment.investorCnic || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Plan Name:</span>
                    <p className="font-medium">{selectedEnrollment.planName || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Enrollment Date:</span>
                    <p className="font-medium">{formatDate(selectedEnrollment.enrollmentDate)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Current Closing Date:</span>
                    <p className="font-medium">
                      {selectedEnrollment.closingDate ? formatDate(selectedEnrollment.closingDate) : <span className="text-gray-400 italic">Not set</span>}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Investor Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="investorName"
                    value={editFormData.investorName}
                    onChange={handleEditInputChange}
                    placeholder="Enter investor name"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isUpdating}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Deposit <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      name="totalDeposit"
                      value={editFormData.totalDeposit}
                      onChange={handleEditInputChange}
                      placeholder="Enter total deposit"
                      className="w-full border-gray-300 rounded-md"
                      disabled={isUpdating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Years <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      name="numberOfYears"
                      value={editFormData.numberOfYears}
                      onChange={handleEditInputChange}
                      placeholder="Enter number of years"
                      className="w-full border-gray-300 rounded-md"
                      disabled={isUpdating}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Profit
                    </label>
                    <Input
                      type="number"
                      name="totalProfit"
                      value={editFormData.totalProfit}
                      onChange={handleEditInputChange}
                      placeholder="Enter total profit"
                      className="w-full border-gray-300 rounded-md"
                      disabled={isUpdating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Loss
                    </label>
                    <Input
                      type="number"
                      name="totalLoss"
                      value={editFormData.totalLoss}
                      onChange={handleEditInputChange}
                      placeholder="Enter total loss"
                      className="w-full border-gray-300 rounded-md"
                      disabled={isUpdating}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Deduction
                    </label>
                    <Input
                      type="number"
                      name="totalDeduction"
                      value={editFormData.totalDeduction}
                      onChange={handleEditInputChange}
                      placeholder="Enter total deduction"
                      className="w-full border-gray-300 rounded-md"
                      disabled={isUpdating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="w-full outline-none bg-lightGray font-medium p-2.5 text-black border border-gray-300 rounded-md"
                    >

                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Status
                    </label>
                    <select
                      name="paymentStatus"
                      value={editFormData.paymentStatus || 'pending'}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="w-full outline-none bg-lightGray font-medium p-2.5 text-black border border-gray-300 rounded-md"
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="adjusted">Adjusted</option>
                      <option value="partial paid">Partial Paid</option>
                      <option value="re invested">Re Invested</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Closing Date
                  </label>
                  <input
                    type="date"
                    name="closingDate"
                    value={editFormData.closingDate}
                    onChange={handleEditInputChange}
                    disabled={isUpdating}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full outline-none bg-lightGray font-medium p-2.5 text-black border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks (optional)
                  </label>
                  <textarea
                    name="remarks"
                    value={editFormData.remarks}
                    onChange={handleEditInputChange}
                    disabled={isUpdating}
                    rows={3}
                    className="w-full outline-none bg-lightGray font-medium p-2.5 text-black border border-gray-300 rounded-md resize-y"
                    placeholder="Add any remarks about this enrollment"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={closeEditModal}
                  disabled={isUpdating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateEnrollment}
                  disabled={isUpdating || !editFormData.investorName.trim() || !editFormData.status}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isUpdating || !editFormData.investorName.trim() || !editFormData.status
                    ? 'bg-[#00AFEF] cursor-not-allowed opacity-70'
                    : 'bg-[#00AFEF] hover:opacity-90'
                    }`}
                >
                  {isUpdating ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Update Enrollment'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              {deleteConfirmationStep === 1 ? (
                // First Confirmation
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">Confirm Deletion</h3>
                    <button
                      onClick={closeDeleteModal}
                      disabled={isDeleting}
                      className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 text-center mb-2">
                      Are you sure you want to delete this enrollment?
                    </h4>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      This will permanently delete the enrollment for:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        <span className="text-gray-600">Investor:</span> {selectedEnrollment.investorName}
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        <span className="text-gray-600">CNIC:</span> {selectedEnrollment.investorCnic}
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        <span className="text-gray-600">Plan:</span> {selectedEnrollment.planName || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeDeleteModal}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFirstConfirm}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm font-medium text-white bg-[red] hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue
                    </button>
                  </div>
                </>
              ) : (
                // Second Confirmation
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-red-600">Final Confirmation</h3>
                    <button
                      onClick={closeDeleteModal}
                      disabled={isDeleting}
                      className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-bold text-red-600 text-center mb-2">
                      Warning: This action cannot be undone!
                    </h4>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      You are about to permanently delete this enrollment. All data associated with this enrollment will be lost.
                    </p>
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                      <p className="text-sm font-semibold text-red-800 text-center">
                        Are you absolutely sure you want to proceed?
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setDeleteConfirmationStep(1)}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={handleDeleteEnrollment}
                      disabled={isDeleting}
                      className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isDeleting
                        ? 'bg-[red] cursor-not-allowed'
                        : 'bg-[red]'
                        }`}
                    >
                      {isDeleting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Deleting...
                        </span>
                      ) : (
                        'Yes, Delete Permanently'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Super_admin_web_protal>
  );
};

export default SuperCompletedPlan;